import { sendMessage, plotJsxGraph } from '@/api/chat/chat'
import { globalEmit, EVENT_TYPES } from '@/utils/eventBus'
import { $notification, $t, sleep } from '@/utils/index.ts'
import { BUTTON_TEXT, REQUEST_SUCCESS_CODE } from '../config/constant'
import { ChatMessage } from '../store/modules/chat/database'

import { checkUserChatNeedSubscription } from '@/utils/svip'
import { logEvent } from '@/utils/utils.ts'
import i18n from '@/i18n'
import store from '../store'

/** 未登录点击 welcome demo 时写入；登录成功后在欢迎页恢复发送。关闭登录框必须清除，否则会「取消后再登录」误触发 demo。 */
export const CHAT_PENDING_DEMO_STORAGE_KEY = 'CHAT_PENDING_DEMO_ITEM'

export const isStreamEnd = (str: string) => {
    return str.includes('[DONE]')
}

export const parseMessageItem = (messageItem: ChatMessage) => {
    if (messageItem.status == 'pending' || messageItem.status == 'receiving' || messageItem.status == 'thinking') {
        messageItem.status = 'error'
        messageItem.errorData = {
            errorMessage: $t('chat.message.empty')
        }
    }

    return messageItem
}

const globalOnStream = (res: any, store: any, options: { isFirstMessageInSession: boolean }) => {
    store.dispatch('chat/changeChatStatus', 'receiving')

    if (res.content) {
        if (isStreamEnd(res.content)) {
            store.dispatch('chat/completeMessage')
            store.dispatch('chat/clearAbortController')

            const lastMessage = store.getters['chat/lastMessage']
            const isQuestionMode = checkIsQuestionMode(lastMessage?.content || '')

            if (isQuestionMode) {
                const shouldGeneratePlot = options?.isFirstMessageInSession

                if (shouldGeneratePlot) {
                    globalCheckGenerateGraphV2(store)
                }
            }
        } else {
            store.dispatch('chat/appendMessageContent', {
                content: res.content
            })
        }
    } else if (res.sessionId || res.sessionMessageId) {
        store.dispatch('chat/updateLastMessage', {
            sessionId: res.sessionId,
            sessionMessageId: res.sessionMessageId
        })
    }
}

const globalCheckGenerateGraphV2 = async (store: any, beginTime: number = Date.now(), sessionMessageCheckId?: string | number): Promise<boolean> => {
    try {
        const sessionMessageId = sessionMessageCheckId || store.getters['chat/lastMessage']?.sessionMessageId
        const throttleMS = 2000
        const maxLoopMS = 30 * 1000
        const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

        // 与 uniapp 对齐：按总耗时窗口控制轮询终止
        if (Math.abs(Date.now() - beginTime) >= maxLoopMS) {
            store.dispatch('chat/updateLastMessage', {
                isGeneratingGif: '0',
                generateImageMathInfo: ''
            })
            return false
        }

        const requestTime = Date.now()
        const res: any = await plotJsxGraph({ sessionMessageId })

        if (res.code !== REQUEST_SUCCESS_CODE) {
            // 如果接口返回错误码（如 500），作为健壮性优化，我们当作网络抖动，继续重试
            const waitMS = Math.max(0, throttleMS - (Date.now() - requestTime))
            await sleep(waitMS)
            return globalCheckGenerateGraphV2(store, beginTime, sessionMessageId)
        }

        const { needsPlotting, status, plotJsxgraphJson } = res.data || {}
        const waitMS = Math.max(0, throttleMS - (Date.now() - requestTime))

        if (needsPlotting === false) {
            store.dispatch('chat/updateLastMessage', {
                isGeneratingGif: '0',
                generateImageMathInfo: ''
            })
            return false
        }

        if (needsPlotting === null) {
            await sleep(waitMS)
            return globalCheckGenerateGraphV2(store, beginTime, sessionMessageId)
        }

        if (needsPlotting === true) {
            store.dispatch('chat/updateLastMessage', {
                isGeneratingGif: '1'
            })

            // 与 uniapp 对齐：等待中或数据缺失时继续轮询
            if (status === 'waiting' || !plotJsxgraphJson) {
                sleep(waitMS).then(() => {
                    globalCheckGenerateGraphV2(store, beginTime, sessionMessageId)
                })
            }

            if (status === 'finished' && plotJsxgraphJson) {
                try {
                    const graphData = JSON.parse(plotJsxgraphJson)
                    const hasElements = Array.isArray(graphData?.elements) && graphData.elements.length > 0

                    if (hasElements) {
                        store.dispatch('chat/updateLastMessage', {
                            isGeneratingGif: '1',
                            generateImageMathInfo: encodeURIComponent(plotJsxgraphJson)
                        })
                    } else {
                        store.dispatch('chat/updateLastMessage', {
                            isGeneratingGif: '0',
                            generateImageMathInfo: ''
                        })
                    }
                } catch (error) {
                    store.dispatch('chat/updateLastMessage', {
                        isGeneratingGif: '0',
                        generateImageMathInfo: ''
                    })
                }
            }

            return true
        }

        return false
    } catch (error) {
        return false
    }
}

const getButtonText = (type: string) => {
    if (type == 'quizMe') {
        return BUTTON_TEXT.quizMe
    } else if (type == 'reAnswer') {
        return BUTTON_TEXT.reAnswer
    }
}

export const sendTextMessage = async (message: { content: string; type?: string; recommend?: boolean; abortController?: AbortController; demoId?: string }, store: any) => {
    if (store.state.chat.chatStatus != 'success') {
        $notification.warning($t('chat.wait'))

        return
    }
    store.dispatch('chat/changeChatStatus', 'thinking')

    const { messageId, historyId } = await store.dispatch('chat/addMessageToHistory', {
        type: message.type || 'text',
        from: 'send',
        content: message.content
    })

    await store.dispatch('chat/addMessageToHistory', {
        from: 'receive',
        content: '',
        relativeId: messageId,
        relativeHId: historyId,
        status: 'thinking'
    })

    globalEmit(EVENT_TYPES.CHAT_SCROLL_BOTTOM)

    const isFirstMessageInSession = !Boolean(store.getters['chat/sessionId'])

    try {
        console.warn('sendTextMessage', message)
        await sendMessage(
            {
                content: message.content,
                recommend: message.recommend,
                buttonText: getButtonText(message.type || ''),
                demoId: message.demoId
            },
            {
                onStream: (res: any) => globalOnStream(res, store, { isFirstMessageInSession }),
                abortController: message.abortController
            }
        )
    } catch (err: any) {
        if (err.msg == 'canceled') {
            console.warn('Canceled receive message')
        } else {
            store.dispatch('chat/completeMessage', {
                status: 'error',
                errorData: {
                    errorMessage: $t('chat.message.empty')
                }
            })
        }
    }
}

export const preSendImageMessage = async (data: any, store: any) => {
    if (store.state.chat.chatStatus != 'success') {
        $notification.warning($t('chat.wait'))

        return
    }

    store.dispatch('chat/changeChatStatus', 'thinking')

    const { messageId, historyId } = await store.dispatch('chat/addMessageToHistory', {
        type: 'image',
        from: 'send',
        image: data.base64
    })

    await store.dispatch('chat/addMessageToHistory', {
        from: 'receive',
        content: '',
        relativeId: messageId,
        relativeHId: historyId,
        status: 'thinking'
    })

    globalEmit(EVENT_TYPES.CHAT_SCROLL_BOTTOM)
}

export const sendImageMessage = async (data: any, store: any) => {
    const isFirstMessageInSession = !Boolean(store.getters['chat/sessionId'])

    store.dispatch('chat/updateLastSendImageMessage', {
        imageId: data.imageId
    })

    try {
        console.warn('sendImageMessage', data)
        console.log('sendMessage-start', store.getters['chat/sessionId'])
        await sendMessage(
            {
                clipImageId: '',
                originImageId: data.imageId,
                buttonText: getButtonText(data.type),
                demoId: data.demoId
            },
            {
                onStream: (res: any) => globalOnStream(res, store, { isFirstMessageInSession }),
                abortController: data.abortController
            }
        )
        console.log('sendMessage-end', store.getters['chat/sessionId'])
    } catch (err: any) {
        if (err.msg == 'canceled') {
            console.warn('Canceled receive message')
        } else {
            store.dispatch('chat/completeMessage', {
                status: 'error',
                errorData: {
                    errorMessage: $t('chat.message.empty')
                }
            })
        }
    }
}

export const checkResInvalid = (text = '') => {
    // 这里只判断这个，有这句话就认为AI无法理解

    const sorryChunk = 'Sorry, the text provided is not valid'
    return text.indexOf(sorryChunk) === 0 || sorryChunk.indexOf(text) === 0
}

export const checkIsQuestionMode = (text: string) => {
    if (!text) return false

    // 1. Strict match for standard English headers (case-insensitive)
    const englishHeadersRegex = /(^|\r?\n)#\s*(Answer|Explanation|Knowledge\s*Points|Quiz\s*problems)\b/i
    if (englishHeadersRegex.test(text)) {
        return true
    }

    // 2. For other languages, match any header starting with '#' that contains non-ASCII characters or common non-English letters.
    // This allows languages like Chinese, Japanese, Thai, etc. to match any '# <Header>' without strict keyword lists.
    // Accented European chars or non-ASCII characters indicate non-English content.
    const nonEnglishHeaderRegex = /(^|\r?\n)#\s*([^a-zA-Z\r\n\s]|[^\x00-\x7F]|\w*[áéíóúüñßöäüæøåčďěňřšťžýžàâæçèéêëîïôœùûüÿçãõ])+/i
    if (nonEnglishHeaderRegex.test(text)) {
        return true
    }

    return false
}

export const CACHE_WELCOME_STREAM_KEY = 'CACHE_WELCOME_STREAM'
// 记录欢迎语缓存对应的界面语言；切换语言后视为失效，强制重新请求
export const CACHE_WELCOME_STREAM_LANG_KEY = 'CACHE_WELCOME_STREAM_LANG'

const getCurrentLocale = (): string => (i18n.global.locale.value as string) || 'en'

export const cacheWelcomeStream = (message: string) => {
    let stream: { [key: string]: string } = {}

    stream[Date.now()] = message

    localStorage.setItem(CACHE_WELCOME_STREAM_KEY, JSON.stringify(stream))
    localStorage.setItem(CACHE_WELCOME_STREAM_LANG_KEY, getCurrentLocale())
}

export const checkCacheWelcomeTimeout3h = () => {
    // 缓存语言与当前界面语言不一致，视为失效，强制重新请求
    const cachedLang = localStorage.getItem(CACHE_WELCOME_STREAM_LANG_KEY) || ''
    if (cachedLang !== getCurrentLocale()) {
        return true
    }

    let lastCacheStr = localStorage.getItem(CACHE_WELCOME_STREAM_KEY) || ''

    try {
        const lastCache = JSON.parse(lastCacheStr)
        const lastCacheTime: number = Number(Object.keys(lastCache)[0])

        if (!lastCacheTime) {
            return true
        } else {
            // 上次操作时间超过3小时，则认为超时
            return Date.now() - lastCacheTime > 1000 * 60 * 60 * 3
            // return Date.now() - lastCacheTime > 1000 * 60 * 1
        }
    } catch (error) {
        return true
    }
}

export const getCacheWelcomeStream = () => {
    let lastCacheStr = localStorage.getItem(CACHE_WELCOME_STREAM_KEY) || ''

    try {
        const lastCache = JSON.parse(lastCacheStr)
        const lastCacheTime: number = Number(Object.keys(lastCache)[0])

        return lastCache[lastCacheTime]
    } catch (error) {
        return ''
    }
}

export const setLastWelcomeStreamToBefore3Hours = () => {
    let newCache: { [key: string]: string } = {}

    newCache[String(Date.now() - 1000 * 60 * 60 * 3)] = 'TEST'

    localStorage.setItem(CACHE_WELCOME_STREAM_KEY, JSON.stringify(newCache))
}

export const formatWelcomeStream = (stream: string) => {
    let text = stream || ''
    // Normalize CRLF to LF to prevent regex failures when stream chunks arrive differently on prod
    text = text.replace(/\r\n/g, '\n')

    if (text.startsWith('# ')) {
        // Strip the first line (H1) and trailing newlines, accounting for end of string in incomplete streams
        text = text.replace(/^#\s*[^\n]+(\n+|$)/, '')
        // Strip the second line (Subtitle) similarly
        text = text.replace(/^[^\n]+(\n+|$)/, '')
    }

    return text.trimStart()
}

export const checkChatLimit = async (options: { abortController?: AbortController } = {}) => {
    const sessionId = store.getters['chat/sessionId']
    if (sessionId) {
        // 有sessionId，则不限制
        return {
            shouldBlockAnswer: false
        }
    }

    const checkUserChatNeedSubscriptionResult = await checkUserChatNeedSubscription(
        {
            functionCode: 'ask_sovi'
        },
        {
            abortController: options.abortController
        }
    )

    // logEvent('web_quota_page_show', {
    //         page_id: paywallConfig?.eventId || '',
    //         page_source: 'chat',
    //         trigger_scene: paywallConfig?.actionCode || '',
    //         trigger_reason: checkUserChatNeedSubscriptionResult?.reason || '',
    //         actions: checkUserChatNeedSubscriptionResult?.paywallList,
    //         page_type: 'default',
    //         version: isV3 ? 'v3' : 'v2',
    //         subject: isEnableSubjectIcon ? paywallConfig?.subjectId || '' : 'general'
    // })

    if (checkUserChatNeedSubscriptionResult.canceled) {
        return {
            shouldBlockAnswer: false,
            canceled: true
        }
    }

    const result = {
        shouldBlockAnswer: checkUserChatNeedSubscriptionResult.show,
        checkResult: checkUserChatNeedSubscriptionResult
    }
    return result
}
